import { useMemo, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeSlug from 'rehype-slug'
import rehypeRaw from 'rehype-raw'
import { motion } from 'framer-motion'
import CodeBlock from '../../components/CodeBlock'
import { DiagramShell } from '../../components/diagrams/DiagramShell'
import { diagramRegistry } from '../../components/diagrams/registry'
import manifest from '../../data/docs-manifest.json'
import { hubContent } from '../../data/hubContent'
import { docMeta } from '../../data/docMeta'

// Load all markdown files as raw strings
const markdownModules = import.meta.glob('../../data/content/*.md', { eager: true, query: '?raw', import: 'default' })

export default function DocPage() {
  const { group, slug } = useParams<{ group: string; slug: string }>()
  
  // Try to find the exact group and slug from the manifest
  let docGroup = manifest.groups.find(g => g.id === group)
  let docItem = docGroup?.items.find(i => i.id === slug)
  
  // Fallback for flat routes if group is missing in URL (e.g. /docs/introduction)
  if (!docItem && !slug) {
    for (const g of manifest.groups) {
      const item = g.items.find(i => i.id === group)
      if (item) {
        docGroup = g
        docItem = item
        break
      }
    }
  }

  const content = useMemo(() => {
    if (!docItem || !(docItem as any).file) return null
    const filePath = `../../data/content/${(docItem as any).file}`
    return (markdownModules[filePath] as unknown as string) || null
  }, [docItem])

  useEffect(() => {
    // Handle hash scrolling for anchor links after render
    if (window.location.hash) {
      setTimeout(() => {
        const id = window.location.hash.substring(1)
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    } else {
      window.scrollTo(0, 0)
    }
  }, [group, slug])

  if (!docItem || !content) {
    if (docGroup && !slug) {
      const hub = group ? hubContent[group] : null
      return (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 100, damping: 20 }}
          className="max-w-[800px] mx-auto py-4"
        >
          {hub && (
            <div className="mb-8 rounded-2xl border border-theme bg-theme-surface/40 p-6 sm:p-8 backdrop-blur-sm">
              <h2 className="text-xl font-semibold text-theme mb-2 tracking-tight">
                {hub.title}
              </h2>
              <p className="text-sm font-medium text-primary mb-3">
                {hub.tagline}
              </p>
              <p className="text-base text-theme-secondary leading-relaxed">
                {hub.description}
              </p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {docGroup.items.map((item: any) => {
              const itemPath = `/docs/${docGroup!.id}/${item.id}`
              const cardDescription = docMeta[itemPath]?.description || `Learn about ${item.label.toLowerCase()} in Solarch's ${docGroup!.label.toLowerCase()} ecosystem.`
              return (
                <Link 
                  key={item.id} 
                  to={itemPath}
                  className="no-underline group p-6 rounded-2xl border border-theme bg-theme-surface hover:border-primary/50 hover:bg-primary/5 transition-all duration-300"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-heading text-lg font-semibold text-theme group-hover:text-primary transition-colors">{item.label}</h3>
                    <div className="h-8 w-8 rounded-full bg-theme flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-colors text-theme-tertiary">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14"></path>
                        <path d="m12 5 7 7-7 7"></path>
                      </svg>
                    </div>
                  </div>
                  <p className="text-sm text-theme-secondary line-clamp-2 leading-relaxed">
                    {cardDescription}
                  </p>
                </Link>
              )
            })}
          </div>
        </motion.div>
      )
    }

    return (
      <div className="py-20 text-center">
        <h1 className="text-2xl font-bold text-theme mb-2">Page Not Found</h1>
        <p className="text-theme-secondary">The documentation page you're looking for doesn't exist.</p>
      </div>
    )
  }

  return (
    <div className="relative flex w-full max-w-full lg:max-w-[1000px] justify-between">
      <motion.div 
        key={slug}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 100, damping: 20 }}
        className="prose prose-invert prose-theme max-w-full lg:max-w-[76ch] mx-auto lg:mx-0 py-8 prose-pre:bg-transparent prose-pre:p-0 prose-p:leading-[1.8] prose-p:text-[1.05rem] prose-headings:tracking-tight prose-table:my-6"
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw, rehypeSlug]}
          components={{
            table({ children, ...props }: any) {
              return (
                <div className="my-8 w-full overflow-x-auto rounded-2xl border border-theme bg-theme-surface/60 shadow-xl backdrop-blur-md">
                  <table className="w-full text-left text-sm border-collapse m-0" {...props}>
                    {children}
                  </table>
                </div>
              )
            },
            thead({ children, ...props }: any) {
              return (
                <thead className="border-b border-theme bg-theme-elevated/70 text-xs font-mono uppercase tracking-wider text-theme-muted" {...props}>
                  {children}
                </thead>
              )
            },
            th({ children, ...props }: any) {
              return (
                <th className="px-5 py-4 font-semibold text-theme whitespace-nowrap" {...props}>
                  {children}
                </th>
              )
            },
            tr({ children, ...props }: any) {
              return (
                <tr className="border-b border-theme/40 last:border-0 hover:bg-theme-elevated/30 transition-colors" {...props}>
                  {children}
                </tr>
              )
            },
            td({ children, ...props }: any) {
              return (
                <td className="px-5 py-3.5 text-sm text-theme-secondary align-middle whitespace-normal leading-relaxed" {...props}>
                  {children}
                </td>
              )
            },
            code(props: any) {
              const { children, className, node, ...rest } = props
              const match = /language-(\w+)/.exec(className || '')
              
              if (!props.inline && match) {
                if (match[1] === 'mermaid') {
                  const currentFile = (docItem as any)?.file || ''
                  const item = diagramRegistry[currentFile] || diagramRegistry['introduction.md']
                  return (
                    <DiagramShell
                      diagramId={item.id}
                      definition={item.definition}
                      lazyImport={item.lazyImport}
                    />
                  )
                }
                return (
                  <CodeBlock
                    lang={match[1] as any}
                    code={String(children).replace(/\n$/, '')}
                  />
                )
              }

              const text = String(children).trim()
              if (text === 'true') {
                return (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-mono font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                    true
                  </span>
                )
              }
              if (text === 'false') {
                return (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-mono font-medium bg-white/5 text-theme-muted border border-white/10">
                    false
                  </span>
                )
              }
              if (text.startsWith('solarch.')) {
                return (
                  <code className="bg-primary/10 border border-primary/20 text-primary px-2 py-0.5 rounded-md font-mono text-[0.88em] font-medium whitespace-nowrap" {...rest}>
                    {children}
                  </code>
                )
              }
              return (
                <code className={`${className || ''} bg-theme-surface border border-theme px-1.5 py-0.5 rounded-md font-mono text-[0.9em] text-theme-code`} {...rest}>
                  {children}
                </code>
              )
            },
            a(props: any) {
              const { href, children, ...rest } = props
              if (href && href.endsWith('.md')) {
                const filename = href.split('/').pop()
                let targetPath = href
                for (const g of manifest.groups) {
                  const item = g.items.find(i => (i as any).file === filename)
                  if (item) {
                    targetPath = `/docs/${g.id}/${item.id}`
                    break
                  }
                }
                return <Link to={targetPath} className="text-primary hover:text-primary/80 decoration-primary/30 hover:decoration-primary transition-colors underline underline-offset-4" {...rest}>{children}</Link>
              }
              if (href && href.startsWith('/')) {
                return <Link to={href} className="text-primary hover:text-primary/80 decoration-primary/30 hover:decoration-primary transition-colors underline underline-offset-4" {...rest}>{children}</Link>
              }
              if (href && href.startsWith('#')) {
                return <a href={href} className="text-primary hover:text-primary/80 decoration-primary/30 hover:decoration-primary transition-colors underline underline-offset-4" {...rest}>{children}</a>
              }
              return <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 decoration-primary/30 hover:decoration-primary transition-colors underline underline-offset-4" {...rest}>{children}</a>
            },
            h1() {
              // Markdown top-level headings are document titles that duplicate DocsLayout H1
              return null
            },
            h2({ children, ...props }: any) {
              return (
                <div className="mt-16 mb-8">
                  <h2 className="font-heading text-2xl lg:text-3xl font-semibold tracking-tight text-theme flex items-center group" {...props}>
                    {children}
                  </h2>
                  {/* Removed hr here */}
                </div>
              )
            },
            blockquote(props: any) {
              const { children, className, node, ...rest } = props;
              
              // Helper to extract text for checking
              const extractText = (node: any): string => {
                if (typeof node === 'string') return node;
                if (typeof node === 'number') return String(node);
                if (Array.isArray(node)) return node.map(extractText).join('');
                if (node?.props?.children) return extractText(node.props.children);
                return '';
              };

              const textContent = extractText(children);
              const match = /\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]/.exec(textContent);

              if (match) {
                const alertType = match[1];
                const colorMap: Record<string, string> = {
                  NOTE: 'border-l-blue-500 bg-blue-500/10 text-blue-400',
                  TIP: 'border-l-emerald-500 bg-emerald-500/10 text-emerald-400',
                  IMPORTANT: 'border-l-primary bg-primary/10 text-primary',
                  WARNING: 'border-l-ember-500 bg-ember-500/10 text-ember-400',
                  CAUTION: 'border-l-red-500 bg-red-500/10 text-red-400'
                };
                
                const titleMap: Record<string, string> = {
                  NOTE: 'Note',
                  TIP: 'Tip',
                  IMPORTANT: 'Important',
                  WARNING: 'Warning',
                  CAUTION: 'Caution'
                };

                // Helper to remove the [!TYPE] tag from children
                const removeTag = (node: any): any => {
                  if (typeof node === 'string') {
                    return node.replace(/\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]\s*/, '');
                  }
                  if (Array.isArray(node)) {
                    return node.map(removeTag);
                  }
                  if (node && node.props && node.props.children) {
                    return {
                      ...node,
                      props: {
                        ...node.props,
                        children: removeTag(node.props.children)
                      }
                    };
                  }
                  return node;
                };

                const cleanedChildren = removeTag(children);

                return (
                  <div 
                    className={`relative my-8 overflow-hidden rounded-xl border border-white/5 border-l-4 p-6 shadow-xl backdrop-blur-sm ${colorMap[alertType] || 'border-l-theme-muted bg-theme-surface text-theme'}`} 
                    style={{ 
                      '--tw-prose-body': 'inherit', 
                      '--tw-prose-bold': 'inherit', 
                      '--tw-prose-code': 'inherit',
                      '--tw-prose-links': 'inherit',
                      '--tw-prose-quotes': 'inherit'
                    } as any}
                    {...rest}
                  >
                    {/* Subtle internal gradient for depth */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/20 pointer-events-none" />
                    
                    <p className="font-bold mb-3 flex items-center gap-2 text-sm tracking-widest uppercase relative z-10 drop-shadow-md">
                      {titleMap[alertType]}
                    </p>
                    <div className="text-[0.95em] leading-relaxed opacity-90 prose-p:my-0 prose-ul:my-2 prose-li:my-1 relative z-10">
                      {cleanedChildren}
                    </div>
                  </div>
                )
              }

              return <blockquote className="border-l-2 border-theme-muted bg-theme-surface p-6 my-8 text-theme-secondary not-italic rounded-r-xl shadow-sm text-lg font-light leading-relaxed" {...rest}>{children}</blockquote>
            }
          }}
        >
          {content}
        </ReactMarkdown>
      </motion.div>
    </div>
  )
}
